import React, { Fragment, useState, useRef, useEffect } from "react";
import { ButtonSecondaryV2, BlockLabel, getBlockLabelProps, useBannerContext, FileUploadWrapper } from "oolib";
import { useTheme } from "styled-components";
import { AudioAction } from "./comps/AudioAction";
import { AudioPlayer } from "./comps/AudioPlayer";
import { TranscriptionDisplay } from "./comps/TranscriptionDisplay";
import { StyledAudioPlayerWrapper } from "./styled.js";

/**
 * @param {Object} [transcription] - Transcription configuration (set on the block in the tpl)
 * @param {boolean} transcription.enable - Enable auto-transcription on upload
 * @param {"sarvam"|"elevenlabs"} [transcription.service="sarvam"] - Which STT service to use
 * @param {boolean} [transcription.diarize=true] - Request speaker diarization
 * @param {boolean} [transcription.translateToEnglish=true] - Request English translation.
 *   Sarvam only — ElevenLabs Scribe cannot translate, so this is ignored when
 *   service is "elevenlabs" (the template builder disables the toggle there).
 *
 * `target` (contentType, docId, valuePath) is derived automatically from
 * `content` and `valuePath` props passed by blockGenerators — no manual wiring needed.
 */
function AudioInput({ id,
    value,
    onChange: parentOnChange,
    readOnly,
    invert,
    isInRTE,
    files,
    onUploadStatusChange,
    transcription,
    content,
    valuePath,
    ...props
}){

  let audioData = value || [];
  const theme = useTheme();
  const [ uploadProgress, setUploadProgress ] = useState(null);
  const { SET_ALERT_BANNER } = useBannerContext();
  const handleUploadProgess = (progress) => setUploadProgress(progress);

  const inputRef = useRef();

  const { mutate: upload, isLoading } =
    theme?.useUploadMedia({
      mediaConfigs: {
        type: "files",
        folderName: "audios",
        mediaType: "audio",
      },
      handleUploadProgess,
    }) || {};

  // Transcription hook (injected via OolibThemeProvider, optional).
  // Falls back to the legacy Sarvam-only hook if the host app injects that one.
  const useTranscriptionHook =
    theme?.useInitiateTranscription || theme?.useInitiateSarvamTranscription;
  const { mutate: initiateTranscription } =
    (transcription?.enable && useTranscriptionHook?.()) || {};

    useEffect(() => {
      onUploadStatusChange?.(isLoading);
    }, [isLoading]);

    const handleUpload = ({ files }) => {
    if (!files) return;
    if (!upload) return;
    upload(files, {
      onSuccess(data) {
        const fileData = data[0].fileData;
        parentOnChange(id, [fileData]);

        // Trigger transcription if enabled and we can derive the target
        if (
          initiateTranscription &&
          transcription?.enable &&
          fileData?.publicUrl &&
          content?._id &&
          content?.meta?.kp_content_type &&
          valuePath
        ) {
          const service = transcription.service || "sarvam";

          initiateTranscription({
            service,
            audioUrl: fileData.publicUrl,
            audioFilename: fileData.name || fileData.originalname || "audio",
            target: {
              contentType: content.meta.kp_content_type,
              // Always reference the original doc — clones (editPublished,
              // moderation) are ephemeral and get deleted on republish/approve.
              docId: content.kp_og_published_doc || content.kp_pre_mod_doc || content._id,
              valuePath,
            },
            options: {
              diarize: transcription.diarize !== false,
              // ElevenLabs Scribe has no translation capability — never ask for it.
              translateToEnglish:
                service === "elevenlabs"
                  ? false
                  : transcription.translateToEnglish !== false,
            },
          });
        }
      },
      onError(err) {
        SET_ALERT_BANNER(
          err.response?.data.errors[0].msg || err.message,
          "red",
          3500
        );
      },
    });
  };

  useEffect(() => {
    if (files) {
      handleUpload({ files });
    }
  }, []);

  // Transcription data from the first audio entry (written by the backend worker)
  const transcriptionData = audioData?.[0]?.transcription;

  return (
    <Fragment>
      <BlockLabel {...getBlockLabelProps(props)} />
      {readOnly ? (
        <>
          <AudioPlayer audioData={audioData}/>
          <TranscriptionDisplay transcriptionData={transcriptionData} content={content} valuePath={valuePath} />
        </>
      ) : audioData?.length === 0  ? (
        <FileUploadWrapper
            height={'18rem'}
            isLoading={isLoading}
            uploadProgress={uploadProgress}
            mediaType={'audio'}
            enableClickUpload={false}
            handleUpload={handleUpload}
        >
            <ButtonSecondaryV2
              onClick={() => inputRef.current.click()}
              icon={"UploadSimple"}
              value={"Upload Audio"}
              accept={"audio/*"}
              invert={invert}
            />
        </FileUploadWrapper>
      ) : (
        audioData && (
          <>
            <StyledAudioPlayerWrapper isInRTE={isInRTE}>
              <AudioPlayer audioData={audioData}/>
              <AudioAction {...{id, isInRTE, inputRef, parentOnChange}}/>
            </StyledAudioPlayerWrapper>
            <TranscriptionDisplay transcriptionData={transcriptionData} content={content} valuePath={valuePath} />
          </>
        )
      )}

      <input
        type="file"
        accept={"audio/*"}
        ref={inputRef}
        style={{ display: "none" }}
        id="AudioField"
        onChange={(v) => {
          handleUpload({files: v.target.files})
        }}
      />
    </Fragment>
  );
};
export default AudioInput;
